/**
 * Есть ли у устройства физическая клавиатура (Type2X_MASTER_PROMPT.md §7).
 *
 * Слепая печать тренируется на настоящих клавишах: экранная клавиатура
 * телефона для этого не годится, и вызвать её нам всё равно нечем — нажатия
 * читаются с документа, а текст упражнения полем ввода не является. До сих
 * пор телефон об этом молчал: экран занятия выглядел рабочим и не отвечал.
 *
 * Определение устроено как обвинение, которое надо доказать: пока устройство
 * не признано устройством без клавиатуры, оно считается обычным. Признаётся
 * только по положительному ответу медиазапроса — грубый указатель и никакого
 * наведения, — а снимается обвинение первым же настоящим нажатием: планшет с
 * чехлом-клавиатурой отвечает на медиазапрос так же, как телефон, и спорить с
 * ним бессмысленно.
 *
 * Передаётся контекстом, как система и фокус-режим (AD-088): экранов набора
 * много, а значение у всех одно.
 */

import { createContext, useContext, useEffect, useState } from 'react';

/** Чем печатают: настоящими клавишами или ничем. */
export type KeyboardPresence = 'physical' | 'touch-only';

export const KeyboardPresenceContext = createContext<KeyboardPresence>('physical');

/** Чем печатают на этом устройстве. */
export function useKeyboardPresence(): KeyboardPresence {
  return useContext(KeyboardPresenceContext);
}

/**
 * Устройство без физической клавиатуры — по признакам, а не по ширине экрана.
 *
 * Узкое окно на компьютере остаётся окном с клавиатурой, и отбирать у него
 * тренировку не за что. `matchMedia` может не быть вовсе (jsdom): тогда ответ
 * отрицательный — доказательств нет.
 */
function touchOnlyDevice(): boolean {
  return globalThis.matchMedia?.('(any-pointer: coarse) and (any-hover: none)').matches === true;
}

/**
 * Присутствие клавиатуры для всего приложения.
 *
 * Первое настоящее нажатие — доказательство, которое сильнее медиазапроса, и
 * действует оно до конца сессии: спрашивать второй раз не за чем.
 */
export function useDetectedKeyboard(): KeyboardPresence {
  const [touchOnly] = useState(touchOnlyDevice);
  const [pressed, setPressed] = useState(false);

  useEffect(() => {
    if (!touchOnly || pressed) return;

    const onKeyDown = (): void => setPressed(true);
    globalThis.document.addEventListener('keydown', onKeyDown);
    return () => globalThis.document.removeEventListener('keydown', onKeyDown);
  }, [touchOnly, pressed]);

  return touchOnly && !pressed ? 'touch-only' : 'physical';
}
